import React, { useEffect, useRef } from "react";

import Config from "../../../Common/constants/EnvironmentConstants";

const ScriptCache = new Map();
const LoadCache = new Set();
const ignoreProps = [
  "onLoad",
  "onReady",
  "dangerouslySetInnerHTML",
  "children",
  "onError",
  "strategy",
  "stylesheets",
];

const loadScript = (props) => {
  const {
    src,
    id,
    onLoad = () => {},
    onReady = null,
    dangerouslySetInnerHTML,
    children = "",
    strategy = "afterInteractive",
    onError,
    stylesheets,
  } = props;
  const cacheKey = id || src;

  if (cacheKey && LoadCache.has(cacheKey)) {
    return;
  }

  if (ScriptCache.has(src)) {
    LoadCache.add(cacheKey);
    ScriptCache.get(src).then(onLoad, onError);
    return;
  }

  const afterLoad = () => {
    if (onReady) {
      onReady();
    }
    LoadCache.add(cacheKey);
  };

  const el = document.createElement("script");
  const loadPromise = new Promise((resolve, reject) => {
    el.addEventListener("load", function (e) {
      resolve();
      if (onLoad) {
        onLoad.call(this, e);
      }
      afterLoad();
    });
    el.addEventListener("error", function (e) {
      reject(e);
    });
  }).catch(function (e) {
    if (onError) {
      onError(e);
    }
  });

  if (dangerouslySetInnerHTML) {
    el.innerHTML = dangerouslySetInnerHTML.__html || "";
    afterLoad();
  } else if (children) {
    el.textContent =
      typeof children === "string"
        ? children
        : Array.isArray(children)
        ? children.join("")
        : "";
    afterLoad();
  } else if (src) {
    el.src = src;
    ScriptCache.set(src, loadPromise);
  }

  for (const [k, value] of Object.entries(props)) {
    if (value === undefined || ignoreProps.includes(k)) {
      continue;
    }
    el.setAttribute(k.toLowerCase(), value);
  }

  if (stylesheets) {
    stylesheets.forEach((stylesheet) => {
      let link = document.createElement("link");
      link.type = "text/css";
      link.rel = "stylesheet";
      link.href = stylesheet;
      document.head.appendChild(link);
    });
  }

  document.body.appendChild(el);
};

function ScriptLoader({
  id,
  src = "",
  onLoad = () => {},
  onReady = null,
  strategy = "afterInteractive",
  onError,
  stylesheets,
  ...restProps
}) {
  const hasOnReadyEffectCalled = useRef(false);
  const hasLoadScriptEffectCalled = useRef(false);

  useEffect(() => {
    const cacheKey = id || src;
    if (!hasOnReadyEffectCalled.current) {
      if (onReady && cacheKey && LoadCache.has(cacheKey)) {
        onReady();
      }
      hasOnReadyEffectCalled.current = true;
    }
  }, [onReady, id, src]);

  useEffect(() => {
    if (!hasLoadScriptEffectCalled.current) {
      if (strategy === "afterInteractive") {
        loadScript({
          id,
          src,
          onLoad,
          onReady,
          strategy,
          onError,
          stylesheets,
          ...restProps,
        });
      } else if (strategy === "lazyOnload") {
        if (document.readyState === "complete") {
          loadScript({
            id,
            src,
            onLoad,
            onReady,
            strategy,
            onError,
            stylesheets,
            ...restProps,
          });
        } else {
          window.addEventListener("load", () => {
            loadScript({
              id,
              src,
              onLoad,
              onReady,
              strategy,
              onError,
              stylesheets,
              ...restProps,
            });
          });
        }
      }
      hasLoadScriptEffectCalled.current = true;
    }
  }, [id, src, onLoad, onReady, strategy, onError, stylesheets, restProps]);

  return null;
}

export default function RenderScripts() {
  const scripts = [
    {
      src: Config.FORMS_RUNTIME_URL,
    },
    {
      src: Config.FORMS_POLYFILLS_URL,
    },
    {
      src: Config.FORMS_BOOK_A_FREE_SESSION_URL,
    },
  ];
  return scripts.map((script, index) => (
    <ScriptLoader key={index} src={script.src} strategy={script.strategy} type="module" />
  ));
}
